import { proxyCustomElement, HTMLElement, createEvent, h, Fragment, Host } from '@stencil/core/internal/client';
import { s as setInputRef, g as getError, a as getValidityObject, c as checkValidity, r as reportValidity, b as setCustomValidity, w as watchError, d as watchWarning, e as getHostAttributes, f as getFormFieldAttributes } from './index7.js';
import { a as toValue, t as toLabel } from './index5.js';
import { i as isOtherOption } from './index6.js';
import { g as getDataAttributes } from './data-attributes.js';
import { H as HiddenHTMLSelect } from './hidden-html-select.js';
import { d as defineCustomElement$7 } from './box.js';
import { d as defineCustomElement$6 } from './check-input.js';
import { d as defineCustomElement$5 } from './form-field.js';
import { d as defineCustomElement$4 } from './icon.js';
import { d as defineCustomElement$3 } from './status.js';
import { d as defineCustomElement$2 } from './tooltip.js';
import { d as defineCustomElement$1 } from './tooltip-label.js';

const checkInputGroupCss = ":host{-webkit-font-smoothing:antialiased;-moz-osx-font-smoothing:grayscale;box-sizing:border-box;display:flex;flex:none;font-family:Open Sans;font-size:.875rem;font-weight:400;letter-spacing:-.0004em;line-height:1.25rem;line-height:normal;max-width:100%;width:100%}:host *,:host :after,:host :before{box-sizing:inherit}:host img{flex:none;overflow:hidden;text-overflow:ellipsis;white-space:pre-line}.inner{display:flex;gap:.75rem}span.header{color:var(--jw-theme-form-label-color);font-family:Open Sans;font-size:.875rem;font-weight:600;letter-spacing:-.0004em;line-height:1.25rem;margin-bottom:-.75rem;margin-top:-.75rem;padding-bottom:.25rem;padding-top:1rem}span.header:first-child{margin-top:0;padding-top:0}span.header:last-child{margin-bottom:0;padding-bottom:0}:host([orientation=horizontal]) .inner{flex-direction:row}:host([orientation=horizontal]) wui-check-input{flex:1}:host([orientation=vertical]) .inner{flex-direction:column}:host([orientation=vertical]) wui-check-input{width:100%}";
const WuiCheckInputGroupStyle0 = checkInputGroupCss;

const WuiCheckInputGroup = /*@__PURE__*/ proxyCustomElement(class WuiCheckInputGroup extends HTMLElement {
    constructor() {
        super();
        this.__registerHost();
        this.__attachShadow();
        this.validityChecked = createEvent(this, "validityChecked", 7);
        this.validityReported = createEvent(this, "validityReported", 7);
        this.setInputRef = setInputRef(this);
        this.dirty = false;
        this.getError = getError(this);
        this.handleInput = (e, option) => {
            // Stop the inner input event from bubbling up
            e.stopPropagation();
            if (this.disabled) {
                return;
            }
            const val = toValue(option);
            if (typeof val === 'undefined') {
                return;
            }
            const newValue = [...(this.value ?? [])];
            if (newValue.includes(val)) {
                this.value = newValue.filter((v) => v !== val);
            }
            else {
                this.value = [...newValue, val];
            }
            this.dirty = true;
            this.checkValidity();
            this.el.dispatchEvent(new window.Event('input', { bubbles: true }));
        };
        this.value = undefined;
        this.required = false;
        this.error = undefined;
        this.warning = undefined;
        this.validate = undefined;
        this.validity = getValidityObject();
        this.validationMessage = '';
        this.customValidation = undefined;
        this.checkValidity = checkValidity(this);
        this.reportValidity = reportValidity(this);
        this.setCustomValidity = setCustomValidity(this);
        this.showValidity = false;
        this.formField = undefined;
        this.options = [];
        this.orientation = 'vertical';
        this.disabled = false;
        this.name = undefined;
        this.size = 'md';
    }
    _watchError(...args) {
        watchError(this, ...args);
    }
    _watchWarning(...args) {
        watchWarning(this, ...args);
    }
    renderInner() {
        return (h(Fragment, null, h(HiddenHTMLSelect, { ref: this.setInputRef, value: this.value, options: this.options, required: !!this.required, disabled: this.disabled, multiple: true, name: this.name }), h("div", { class: "inner" }, this.options?.map((option, idx) => {
            if (isOtherOption(option)) {
                return (h("span", { class: "header", key: idx }, option.label));
            }
            const label = toLabel(option);
            const value = toValue(option);
            const checked = (this.value || []).includes(value);
            const disabled = typeof option === 'object' ? option.disabled : undefined;
            const description = typeof option === 'object' ? option.description : undefined;
            return (h("wui-check-input", { key: idx, description: this.size === 'md' ? undefined : description, formField: this.size === 'md' && description ? { description } : undefined, checked: checked, disabled: this.disabled || disabled, onChange: (e) => {
                    // Prevent change event from bubbling up
                    e.stopPropagation();
                }, onInput: (e) => this.handleInput(e, option), value: value, label: label, name: this.name ? `${this.name}[]` : undefined, ...(typeof option === 'object' ? getDataAttributes(option) : {}) }));
        }))));
    }
    render() {
        return (h(Host, { key: '3a194688fda1e3de5fe19f6a08656dd81d5516f4', ...getHostAttributes(this) }, h("wui-form-field", { key: 'f783f9978225cd49d8a6b3e41f89b62e15e47379', ...getFormFieldAttributes(this) }, this.renderInner())));
    }
    get el() { return this; }
    static get watchers() { return {
        "error": ["_watchError"],
        "warning": ["_watchWarning"]
    }; }
    static get style() { return WuiCheckInputGroupStyle0; }
}, [1, "wui-check-input-group", {
        "value": [1040],
        "required": [8],
        "error": [8],
        "warning": [8],
        "validate": [16],
        "validity": [1040],
        "validationMessage": [1025, "validation-message"],
        "checkValidity": [16],
        "reportValidity": [16],
        "setCustomValidity": [16],
        "formField": [16],
        "options": [16],
        "orientation": [513],
        "disabled": [516],
        "name": [1],
        "size": [513],
        "customValidation": [32],
        "showValidity": [32]
    }, undefined, {
        "error": ["_watchError"],
        "warning": ["_watchWarning"]
    }]);
function defineCustomElement() {
    if (typeof customElements === "undefined") {
        return;
    }
    const components = ["wui-check-input-group", "wui-box", "wui-check-input", "wui-form-field", "wui-icon", "wui-status", "wui-tooltip", "wui-tooltip-label"];
    components.forEach(tagName => { switch (tagName) {
        case "wui-check-input-group":
            if (!customElements.get(tagName)) {
                customElements.define(tagName, WuiCheckInputGroup);
            }
            break;
        case "wui-box":
            if (!customElements.get(tagName)) {
                defineCustomElement$7();
            }
            break;
        case "wui-check-input":
            if (!customElements.get(tagName)) {
                defineCustomElement$6();
            }
            break;
        case "wui-form-field":
            if (!customElements.get(tagName)) {
                defineCustomElement$5();
            }
            break;
        case "wui-icon":
            if (!customElements.get(tagName)) {
                defineCustomElement$4();
            }
            break;
        case "wui-status":
            if (!customElements.get(tagName)) {
                defineCustomElement$3();
            }
            break;
        case "wui-tooltip":
            if (!customElements.get(tagName)) {
                defineCustomElement$2();
            }
            break;
        case "wui-tooltip-label":
            if (!customElements.get(tagName)) {
                defineCustomElement$1();
            }
            break;
    } });
}
defineCustomElement();

export { WuiCheckInputGroup as W, defineCustomElement as d };

//# sourceMappingURL=check-input-group.js.map